import { DiscoveryApi, FetchApi, IdentityApi } from '@backstage/core-plugin-api';
import { AnalyticsApi, AnalyticsData, PageStats, SummaryStats, PageDetailStats, UserDetailStats } from './types';

export class AnalyticsClient implements AnalyticsApi {
  private readonly discoveryApi: DiscoveryApi;
  private readonly fetchApi: FetchApi;
  private readonly identityApi: IdentityApi;

  constructor(options: {
    discoveryApi: DiscoveryApi;
    fetchApi: FetchApi;
    identityApi: IdentityApi;
  }) {
    this.discoveryApi = options.discoveryApi;
    this.fetchApi = options.fetchApi;
    this.identityApi = options.identityApi;
  }

  async trackPageView(page: string): Promise<void> {
    const baseUrl = await this.discoveryApi.getBaseUrl('analytics');
    const { token } = await this.identityApi.getCredentials();

    await this.fetchApi.fetch(`${baseUrl}/track`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token && { Authorization: `Bearer ${token}` }),
      },
      body: JSON.stringify({ page }),
    });
  }

  async getAnalytics(startDate?: string, endDate?: string): Promise<AnalyticsData> {
    const baseUrl = await this.discoveryApi.getBaseUrl('analytics');
    const { token } = await this.identityApi.getCredentials();

    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);

    const response = await this.fetchApi.fetch(
      `${baseUrl}/analytics?${params.toString()}`,
      {
        headers: {
          ...(token && { Authorization: `Bearer ${token}` }),
        },
      },
    );

    if (!response.ok) {
      throw new Error(`Failed to fetch analytics: ${response.statusText}`);
    }

    return await response.json();
  }

  async getTopPages(limit = 10, startDate?: string, endDate?: string): Promise<{ topPages: PageStats[] }> {
    const baseUrl = await this.discoveryApi.getBaseUrl('analytics');
    const { token } = await this.identityApi.getCredentials();

    const params = new URLSearchParams();
    params.append('limit', limit.toString());
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);

    const response = await this.fetchApi.fetch(
      `${baseUrl}/top-pages?${params.toString()}`,
      {
        headers: {
          ...(token && { Authorization: `Bearer ${token}` }),
        },
      },
    );

    if (!response.ok) {
      throw new Error(`Failed to fetch top pages: ${response.statusText}`);
    }

    return await response.json();
  }

  async getSummaryStats(startDate?: string, endDate?: string): Promise<SummaryStats> {
    const baseUrl = await this.discoveryApi.getBaseUrl('analytics');
    const { token } = await this.identityApi.getCredentials();

    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);

    const response = await this.fetchApi.fetch(
      `${baseUrl}/summary?${params.toString()}`,
      {
        headers: {
          ...(token && { Authorization: `Bearer ${token}` }),
        },
      },
    );

    if (!response.ok) {
      throw new Error(`Failed to fetch summary stats: ${response.statusText}`);
    }

    return await response.json();
  }

  async getPageDetails(page: string, startDate?: string, endDate?: string): Promise<PageDetailStats> {
    const baseUrl = await this.discoveryApi.getBaseUrl('analytics');
    const { token } = await this.identityApi.getCredentials();

    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);

    const encodedPage = encodeURIComponent(page);
    const response = await this.fetchApi.fetch(
      `${baseUrl}/page-details/${encodedPage}?${params.toString()}`,
      {
        headers: {
          ...(token && { Authorization: `Bearer ${token}` }),
        },
      },
    );

    if (!response.ok) {
      throw new Error(`Failed to fetch page details: ${response.statusText}`);
    }

    return await response.json();
  }

  async getUserDetails(user: string, startDate?: string, endDate?: string): Promise<UserDetailStats> {
    const baseUrl = await this.discoveryApi.getBaseUrl('analytics');
    const { token } = await this.identityApi.getCredentials();

    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);

    const encodedUser = encodeURIComponent(user);
    const response = await this.fetchApi.fetch(
      `${baseUrl}/user-details/${encodedUser}?${params.toString()}`,
      {
        headers: {
          ...(token && { Authorization: `Bearer ${token}` }),
        },
      },
    );

    if (!response.ok) {
      throw new Error(`Failed to fetch user details: ${response.statusText}`);
    }

    return await response.json();
  }
}
